import { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { 
  Calendar, ArrowRight, ArrowLeft, User, Share2, Rss, 
  Facebook, Twitter, Linkedin, Mail, ChevronLeft, ChevronRight,
  Filter, X, ExternalLink
} from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { RESOURCES_STICKY_TOP_CLASS } from '../constants/resources';
import {
  findNewsArticleByRouteId,
  getNewsArticlePath,
  NEWS_FALLBACK_ARTICLES,
  NATIONAL_NEWS_FALLBACK,
  NEWS_SOURCE_OPTIONS,
  filterNewsBySource,
  isExternalNewsArticle,
  getExternalNewsUrl,
  CPRE_NATIONAL_NEWS_URL,
  isNationalFundraisingAppealArticle,
  getNationalFundraisingAppealMailtoUrl,
  NEWS_SOURCE,
  sortNewsByDateDesc,
  resolveNewsImageUrl,
} from '../utils/newsArticle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOCAL_DEV_API = 'http://localhost:8001/api';

const getNewsApiBases = () => {
  if (process.env.NODE_ENV === 'development' && API !== LOCAL_DEV_API) {
    return [LOCAL_DEV_API, API];
  }
  return [API];
};

const fetchNewsArticles = async () => {
  for (const base of getNewsApiBases()) {
    try {
      const response = await axios.get(`${base}/news`, { params: { limit: 50 } });
      return response.data || [];
    } catch (error) {
      console.warn(`Failed to fetch news from ${base}:`, error.message);
    }
  }
  return null;
};

const NEWS_FILTERS_ID = 'news-filters';

const BOXING_CLEVER_ARTICLE_TITLE =
  'Boxing Clever: CPRE Launches Lifesaving Village Phone Box Competition';
const CPRE_WEBSITE_IMAGES = '/images/cpre images for website';

// Formatted text component to handle bold, links, etc.
const FormattedText = ({ text }) => {
  // Split text by bold markers and URLs
  const parts = text.split(/(\*\*[^*]+\*\*|https?:\/\/[^\s]+)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i} className="font-semibold text-cpre-new-yew">{part.slice(2, -2)}</strong>;
        }
        if (part.match(/^https?:\/\//)) {
          return (
            <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-cpre-hill hover:text-cpre-new-yew underline break-all">
              {part}
            </a>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
};

const renderNewsArticleParagraph = (paragraph, index) => {
  if (paragraph.trim().startsWith('- ')) {
    const items = paragraph.split('\n').filter((l) => l.trim().startsWith('- '));
    return (
      <ul key={index} className="list-disc pl-6 mb-6 space-y-2">
        {items.map((item, i) => (
          <li key={i} className="text-cpre-new-yew/80 leading-relaxed">
            <FormattedText text={item.replace(/^-\s*/, '')} />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <p key={index} className="text-cpre-new-yew/80 leading-relaxed mb-6">
      <FormattedText text={paragraph} />
    </p>
  );
};

const renderNewsArticleBodyContent = (article) => {
  const paragraphs = article.content.split('\n\n');

  if (article.title !== BOXING_CLEVER_ARTICLE_TITLE) {
    return paragraphs.map(renderNewsArticleParagraph);
  }

  return paragraphs.map((paragraph, index) => {
    if (paragraph.startsWith('The closing date for entries')) {
      return (
        <div key={`boxing-clever-closing-${index}`} className="clearfix mb-6">
          <img
            src={`${CPRE_WEBSITE_IMAGES}/35.jpg`}
            alt="CPRE centenary show stand"
            className="float-left w-full sm:w-2/3 max-w-xl rounded-lg mr-6 mb-4"
          />
          <p className="text-cpre-new-yew/80 leading-relaxed">
            <FormattedText text={paragraph} />
          </p>
        </div>
      );
    }

    return renderNewsArticleParagraph(paragraph, index);
  });
};

const categoryColors = {
  centenary: 'bg-cpre-harvest-gold text-cpre-new-yew hover:bg-cpre-autumn-leaf hover:text-white',
  planning: 'bg-cpre-harvest-gold text-cpre-new-yew hover:bg-cpre-autumn-leaf hover:text-white',
  projects: 'bg-cpre-harvest-gold text-cpre-new-yew hover:bg-cpre-autumn-leaf hover:text-white',
  nature: 'bg-cpre-harvest-gold text-cpre-new-yew hover:bg-cpre-autumn-leaf hover:text-white',
  general: 'bg-cpre-harvest-gold text-cpre-new-yew hover:bg-cpre-autumn-leaf hover:text-white',
  campaigns: 'bg-cpre-harvest-gold text-cpre-new-yew hover:bg-cpre-autumn-leaf hover:text-white',
  events: 'bg-cpre-hill text-white',
  announcement: 'bg-cpre-autumn-leaf text-white',
  'press-release': 'bg-cpre-rose text-white',
};

const categories = [
  { id: 'all', name: 'All News', color: 'bg-cpre-new-yew text-white' },
  { id: 'centenary', name: 'Centenary', color: categoryColors.centenary },
  { id: 'planning', name: 'Planning', color: categoryColors.planning },
  { id: 'projects', name: 'Projects', color: categoryColors.projects },
  { id: 'nature', name: 'Nature', color: categoryColors.nature },
  { id: 'general', name: 'General', color: categoryColors.general },
  { id: 'campaigns', name: 'Campaigns', color: categoryColors.campaigns },
];

const BOXING_CLEVER_CAROUSEL_IMAGE = '/images/cpre images for website/38.jpg';

const getStoryCarouselImageClassName = (imageUrl = '') => {
  if (imageUrl === BOXING_CLEVER_CAROUSEL_IMAGE || imageUrl.endsWith('38.jpg')) {
    return 'w-full h-full object-cover object-[78%_42%] transition-opacity duration-700';
  }
  return 'w-full h-full object-cover transition-opacity duration-700';
};

// Story Carousel Component
const StoryCarousel = ({ stories }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (isAutoPlaying && stories.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % stories.length);
      }, 5000);
    }
    return () => clearInterval(intervalRef.current);
  }, [isAutoPlaying, stories.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const nextSlide = () => goToSlide((currentIndex + 1) % stories.length);
  const prevSlide = () => goToSlide((currentIndex - 1 + stories.length) % stories.length);

  if (!stories.length) return null;

  const currentStory = stories[currentIndex];

  return (
    <section className="relative bg-cpre-new-yew overflow-hidden" data-testid="story-carousel">
      <div className="relative h-[500px] md:h-[600px]">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={resolveNewsImageUrl(currentStory.image_url) || '/images/cpre images for website/19.jpg'}
            alt={currentStory.title}
            className={getStoryCarouselImageClassName(currentStory.image_url)}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-cpre-new-yew via-cpre-new-yew/60 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative container-custom h-full flex flex-col justify-end pb-16 md:pb-20">
          <div className="max-w-3xl">
            <Badge className={`${categoryColors[currentStory.category] || categoryColors.general} mb-4 text-sm`}>
              {currentStory.category}
            </Badge>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-white mb-4 leading-tight">
              {currentStory.title}
            </h2>
            <p className="text-white/90 text-lg md:text-xl mb-6 line-clamp-2">
              {currentStory.excerpt}
            </p>
            <div className="flex items-center gap-6">
              <Link to={getNewsArticlePath(currentStory)}>
                <Button className="bg-cpre-harvest-gold hover:bg-cpre-harvest-gold/90 text-cpre-new-yew rounded-full px-6 font-semibold">
                  Read Full Story
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <span className="text-white/70 text-sm flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {new Date(currentStory.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
            </div>
          </div>
        </div>

        {/* Navigation Arrows */}
        {stories.length > 1 && (
          <>
            <button
              onClick={prevSlide}
              className="absolute left-4 top-[calc(50%-1in)] -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-colors"
              aria-label="Previous story"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 top-[calc(50%-1in)] -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-colors"
              aria-label="Next story"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}

        {/* Dots */}
        {stories.length > 1 && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
            {stories.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentIndex ? 'bg-cpre-harvest-gold w-8' : 'bg-white/50 hover:bg-white/70'
                }`}
                aria-label={`Go to story ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// Social Share Buttons Component
const SocialShareButtons = ({ url, title }) => {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const shareLinks = [
    {
      name: 'Facebook',
      icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      color: 'hover:bg-blue-600 hover:text-white',
    },
    {
      name: 'Twitter',
      icon: Twitter,
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      color: 'hover:bg-sky-500 hover:text-white',
    },
    {
      name: 'LinkedIn',
      icon: Linkedin,
      href: `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedTitle}`,
      color: 'hover:bg-blue-700 hover:text-white',
    },
    {
      name: 'Email',
      icon: Mail,
      href: `mailto:?subject=${encodedTitle}&body=Check out this article: ${encodedUrl}`,
      color: 'hover:bg-cpre-new-yew hover:text-white',
    },
  ];

  const handleCopyLink = () => {
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard!');
  };

  return (
    <div className="flex items-center gap-2" data-testid="social-share-buttons">
      <span className="text-sm text-muted-foreground mr-2">Share:</span>
      {shareLinks.map(({ name, icon: Icon, href, color }) => (
        <a
          key={name}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={`w-9 h-9 rounded-full bg-cpre-pebble/50 flex items-center justify-center text-cpre-new-yew transition-colors ${color}`}
          aria-label={`Share on ${name}`}
          data-testid={`share-${name.toLowerCase()}`}
        >
          <Icon className="w-4 h-4" />
        </a>
      ))}
      <button
        onClick={handleCopyLink}
        className="w-9 h-9 rounded-full bg-cpre-pebble/50 flex items-center justify-center text-cpre-new-yew hover:bg-cpre-mint hover:text-cpre-new-yew transition-colors"
        aria-label="Copy link"
        data-testid="share-copy-link"
      >
        <Share2 className="w-4 h-4" />
      </button>
    </div>
  );
};

// Newsletter Signup Component for News Page
const NewsletterSignup = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success('Successfully subscribed to newsletter!');
      setEmail('');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-cpre-pebble rounded-2xl p-6 md:p-8" data-testid="newsletter-signup">
      <h3 className="font-heading font-bold text-xl text-cpre-new-yew mb-2">Stay Informed</h3>
      <p className="text-cpre-new-yew/70 text-sm mb-4">
        Get the latest countryside news, announcements, and event recaps delivered to your inbox.
      </p>
      <form onSubmit={handleSubmit} className="flex gap-2">
        <Input
          type="email"
          placeholder="Your email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="flex-1 bg-white border-cpre-new-yew/20 focus:border-cpre-new-yew"
          required
          data-testid="newsletter-email-input"
        />
        <Button 
          type="submit" 
          disabled={loading}
          className="bg-cpre-new-yew hover:bg-cpre-new-yew/90 text-white rounded-lg px-6"
          data-testid="newsletter-submit-btn"
        >
          {loading ? 'Subscribing...' : 'Subscribe'}
        </Button>
      </form>
    </div>
  );
};

// RSS Feed Link Component
const RSSFeedLink = () => (
  <button
    type="button"
    onClick={() =>
      toast.info('Coming Soon', {
        description: 'RSS feed will be available here soon.',
      })
    }
    className="inline-flex items-center gap-2 text-cpre-new-yew hover:text-cpre-hill transition-colors text-sm font-medium"
    data-testid="rss-feed-link"
  >
    <Rss className="w-4 h-4" />
    RSS Feed
  </button>
);

// Main News Page Component
const NewsPage = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('local');

  useEffect(() => {
    const fetchNews = async () => {
      const articles = await fetchNewsArticles();
      if (articles) {
        setNews(sortNewsByDateDesc(articles));
      } else {
        console.error('Failed to fetch news from all API endpoints');
        setNews(sortNewsByDateDesc([...NEWS_FALLBACK_ARTICLES, ...NATIONAL_NEWS_FALLBACK]));
      }
      setLoading(false);
    };
    fetchNews();
  }, []);

  const sourceFilteredNews = sortNewsByDateDesc(filterNewsBySource(news, sourceFilter));
  const featuredNews = sortNewsByDateDesc(filterNewsBySource(news, NEWS_SOURCE.LOCAL)).slice(0, 3);

  const filteredNews = sortNewsByDateDesc(
    selectedCategory === 'all'
      ? sourceFilteredNews
      : sourceFilteredNews.filter((article) => article.category === selectedCategory)
  );

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <Layout>
      <div data-testid="news-page">
        {/* Story Carousel */}
        {!loading && featuredNews.length > 0 && (
          <StoryCarousel stories={featuredNews} />
        )}

        {/* Category Filters & RSS */}
        <div
          id={NEWS_FILTERS_ID}
          className="scroll-mt-[6.5rem] md:scroll-mt-[7.5rem]"
        >
          <div
            className={`sticky ${RESOURCES_STICKY_TOP_CLASS} z-40 bg-white border-b border-stone-200 shadow-sm`}
          >
            <div className="container-custom py-4 md:py-5">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 min-w-0">
                {/* Local / National Toggle */}
                <div className="flex gap-1 p-1 bg-stone-100 rounded-full flex-shrink-0" data-testid="news-source-toggle">
                  {NEWS_SOURCE_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setSourceFilter(option.id)}
                      className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                        sourceFilter === option.id
                          ? 'bg-cpre-new-yew text-white'
                          : 'text-stone-600 hover:bg-stone-200'
                      }`}
                      data-testid={`source-filter-${option.id}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

              {/* Category Filters */}
              <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-2 md:pb-0 min-w-0" data-testid="category-filters">
                <Filter className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                      selectedCategory === category.id
                        ? category.color
                        : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                    }`}
                    data-testid={`filter-${category.id}`}
                  >
                    {category.name}
                    {selectedCategory === category.id && selectedCategory !== 'all' && (
                      <X className="w-3 h-3 ml-1 inline" onClick={(e) => { e.stopPropagation(); setSelectedCategory('all'); }} />
                    )}
                  </button>
                ))}
              </div>
              </div>
              
              {/* RSS Feed Link */}
              <RSSFeedLink />
            </div>
            </div>
          </div>
        </div>

        {/* News Grid with Newsletter Sidebar */}
        <section className="section-padding pt-8 md:pt-10 bg-cpre-heritage-white">
          <div className="container-custom">
            <div className="flex flex-col lg:flex-row gap-12">
              {/* Main News Grid */}
              <div className="flex-1">
                <div className="flex items-center justify-between mb-8">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-cpre-new-yew">
                    {selectedCategory === 'all' ? 'Latest News' : categories.find(c => c.id === selectedCategory)?.name}
                  </h2>
                  <span className="text-sm text-muted-foreground">
                    {filteredNews.length} {filteredNews.length === 1 ? 'article' : 'articles'}
                  </span>
                </div>

                {loading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {[1, 2, 3, 4].map(i => (
                      <div key={i} className="h-96 bg-stone-100 rounded-2xl animate-pulse" />
                    ))}
                  </div>
                ) : filteredNews.length === 0 ? (
                  <div className="text-center py-16">
                    <p className="text-muted-foreground">No articles found in this category.</p>
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={() => setSelectedCategory('all')}
                    >
                      View All News
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {filteredNews.map((article, index) => {
                      const card = (
                        <Card 
                          className="bg-white border-stone-200 overflow-hidden group h-full hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                          data-testid={`news-card-${index}`}
                        >
                          <div className="aspect-[16/10] overflow-hidden relative">
                            <img
                              src={resolveNewsImageUrl(article.image_url) || '/images/cpre images for website/17.jpg'}
                              alt={article.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                            <Badge className={`${categoryColors[article.category] || categoryColors.general} absolute top-4 left-4 text-xs font-medium`}>
                              {article.category}
                            </Badge>
                          </div>
                          <CardContent className="p-6">
                            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatDate(article.created_at)}
                              </span>
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {article.author}
                              </span>
                            </div>
                            <h3 className="font-heading font-semibold text-lg text-cpre-new-yew mb-2 line-clamp-2 group-hover:text-cpre-hill transition-colors duration-200">
                              {article.title}
                            </h3>
                            <p className="text-muted-foreground text-sm line-clamp-2">
                              {article.excerpt}
                            </p>
                            {!(
                              isExternalNewsArticle(article) &&
                              isNationalFundraisingAppealArticle(article)
                            ) && (
                              <div className="mt-4 flex items-center text-cpre-new-yew text-sm font-medium group-hover:text-cpre-hill">
                                Read more
                                <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );

                      if (
                        isExternalNewsArticle(article) &&
                        isNationalFundraisingAppealArticle(article)
                      ) {
                        return (
                          <Card
                            key={article.id}
                            className="bg-white border-stone-200 overflow-hidden group h-full hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                            data-testid={`news-card-${index}`}
                          >
                            <a href={getNationalFundraisingAppealMailtoUrl()} className="block">
                              <div className="aspect-[16/10] overflow-hidden relative">
                                <img
                                  src={resolveNewsImageUrl(article.image_url) || '/images/cpre images for website/17.jpg'}
                                  alt={article.title}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                />
                                <Badge
                                  className={`${categoryColors[article.category] || categoryColors.general} absolute top-4 left-4 text-xs font-medium`}
                                >
                                  {article.category}
                                </Badge>
                              </div>
                              <CardContent className="p-6 pb-0">
                                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {formatDate(article.created_at)}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <User className="w-3 h-3" />
                                    {article.author}
                                  </span>
                                </div>
                                <h3 className="font-heading font-semibold text-lg text-cpre-new-yew mb-2 line-clamp-2 group-hover:text-cpre-hill transition-colors duration-200">
                                  {article.title}
                                </h3>
                                <p className="text-muted-foreground text-sm line-clamp-2">
                                  {article.excerpt}
                                </p>
                              </CardContent>
                            </a>
                            <CardContent className="px-6 pb-6 pt-4">
                              <a
                                href={CPRE_NATIONAL_NEWS_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center text-cpre-new-yew text-sm font-medium hover:text-cpre-hill"
                              >
                                Read more
                                <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                              </a>
                            </CardContent>
                          </Card>
                        );
                      }

                      if (isExternalNewsArticle(article)) {
                        return (
                          <a
                            key={article.id}
                            href={getExternalNewsUrl(article)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {card}
                          </a>
                        );
                      }

                      return (
                        <Link key={article.id} to={getNewsArticlePath(article)}>
                          {card}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <aside className="lg:w-80 flex-shrink-0 space-y-8">
                {/* Newsletter Signup */}
                <NewsletterSignup />

                {/* Quick Links */}
                <div className="bg-white rounded-2xl p-6 border border-stone-200">
                  <h3 className="font-heading font-bold text-lg text-cpre-new-yew mb-4">Quick Links</h3>
                  <ul className="space-y-3">
                    <li>
                      <Link to="/centenary" className="flex items-center gap-2 text-cpre-new-yew/80 hover:text-cpre-new-yew transition-colors">
                        <ArrowRight className="w-4 h-4" />
                        Centenary Celebrations
                      </Link>
                    </li>
                    <li>
                      <Link to="/planning" className="flex items-center gap-2 text-cpre-new-yew/80 hover:text-cpre-new-yew transition-colors">
                        <ArrowRight className="w-4 h-4" />
                        Planning Alerts
                      </Link>
                    </li>
                    <li>
                      <Link to="/events" className="flex items-center gap-2 text-cpre-new-yew/80 hover:text-cpre-new-yew transition-colors">
                        <ArrowRight className="w-4 h-4" />
                        Upcoming Events
                      </Link>
                    </li>
                    <li>
                      <a 
                        href="https://www.cpre.org.uk/news/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-cpre-new-yew/80 hover:text-cpre-new-yew transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                        National CPRE News
                      </a>
                    </li>
                  </ul>
                </div>

                {/* Categories Overview */}
                <div className="bg-white rounded-2xl p-6 border border-stone-200">
                  <h3 className="font-heading font-bold text-lg text-cpre-new-yew mb-4">Categories</h3>
                  <ul className="space-y-2">
                    {categories.slice(1).map((category) => {
                      const count = sourceFilteredNews.filter(n => n.category === category.id).length;
                      return (
                        <li key={category.id}>
                          <button
                            onClick={() => setSelectedCategory(category.id)}
                            className="w-full flex items-center justify-between py-2 px-3 rounded-lg hover:bg-cpre-pebble/30 transition-colors"
                          >
                            <span className="text-cpre-new-yew/80">{category.name}</span>
                            <Badge variant="secondary" className="bg-cpre-pebble/50 text-cpre-new-yew">
                              {count}
                            </Badge>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

// Individual News Article Page
const NewsArticlePage = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedNews, setRelatedNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      let resolvedArticle = null;
      let allNews = [];

      try {
        const articleRes = await axios.get(`${API}/news/${encodeURIComponent(id)}`);
        resolvedArticle = articleRes.data;
      } catch (error) {
        console.error('Failed to fetch article by id:', error);
      }

      try {
        const newsRes = await axios.get(`${API}/news`, { params: { limit: 50 } });
        allNews = sortNewsByDateDesc(newsRes.data || []);
        if (!resolvedArticle) {
          const matched = findNewsArticleByRouteId(allNews, id);
          if (matched) {
            resolvedArticle = {
              ...matched,
              content: matched.content || matched.excerpt || '',
            };
          }
        }
      } catch (error) {
        console.error('Failed to fetch news list:', error);
      }

      if (resolvedArticle && isExternalNewsArticle(resolvedArticle)) {
        window.location.replace(getExternalNewsUrl(resolvedArticle));
        return;
      }

      if (!resolvedArticle) {
        const nationalMatch = findNewsArticleByRouteId(NATIONAL_NEWS_FALLBACK, id);
        if (nationalMatch) {
          window.location.replace(getExternalNewsUrl(nationalMatch));
          return;
        }
      }

      if (!resolvedArticle) {
        const fallbackMatch = findNewsArticleByRouteId(NEWS_FALLBACK_ARTICLES, id);
        if (fallbackMatch) {
          resolvedArticle = {
            ...fallbackMatch,
            content: fallbackMatch.content || fallbackMatch.excerpt || '',
          };
        }
      }

      setArticle(resolvedArticle);

      if (resolvedArticle) {
        const sourceList = sortNewsByDateDesc(allNews.length ? allNews : NEWS_FALLBACK_ARTICLES);
        const currentPath = getNewsArticlePath(resolvedArticle);
        const related = sourceList
          .filter((n) => getNewsArticlePath(n) !== currentPath)
          .filter((n) => n.category === resolvedArticle.category)
          .slice(0, 2);
        setRelatedNews(
          related.length
            ? related
            : sourceList.filter((n) => getNewsArticlePath(n) !== currentPath).slice(0, 2)
        );
      } else {
        setRelatedNews([]);
      }

      setLoading(false);
    };
    fetchArticle();
  }, [id]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="section-padding">
          <div className="container-custom max-w-4xl">
            <div className="animate-pulse space-y-8">
              <div className="h-8 w-32 bg-stone-200 rounded" />
              <div className="h-96 bg-stone-200 rounded-2xl" />
              <div className="h-12 w-3/4 bg-stone-200 rounded" />
              <div className="h-4 w-full bg-stone-200 rounded" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!article) {
    return (
      <Layout>
        <div className="section-padding text-center">
          <h2 className="font-heading text-2xl font-bold text-cpre-new-yew">Article not found</h2>
          <Link to="/news">
            <Button className="mt-4 bg-cpre-new-yew hover:bg-cpre-new-yew/90">Back to News</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const currentUrl = window.location.href;

  return (
    <Layout>
      <div data-testid="news-article-page">
        <article className="section-padding">
          <div className="container-custom max-w-4xl">
            {/* Back Link */}
            <Link to="/news">
              <Button variant="ghost" className="text-muted-foreground hover:text-cpre-new-yew mb-8" data-testid="back-to-news">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to News
              </Button>
            </Link>

            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Badge className={`${categoryColors[article.category] || categoryColors.general} rounded-full`}>
                  {article.category}
                </Badge>
                <span className="text-sm text-muted-foreground flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatDate(article.created_at)}
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-cpre-new-yew mb-6">
                {article.title}
              </h1>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <span className="text-muted-foreground flex items-center gap-2">
                  <User className="w-4 h-4" />
                  By {article.author}
                </span>
                <SocialShareButtons url={currentUrl} title={article.title} />
              </div>
            </div>

            {/* Featured Image */}
            <div className="aspect-video rounded-2xl overflow-hidden mb-8 shadow-lg">
              <img
                src={resolveNewsImageUrl(article.image_url) || '/images/cpre images for website/19.jpg'}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Content */}
            <div className="prose prose-stone prose-lg max-w-none mb-12" data-testid="article-content">
              {renderNewsArticleBodyContent(article)}
            </div>

            {/* Bottom Share Section */}
            <div className="border-t border-b border-stone-200 py-6 mb-12">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <span className="font-medium text-cpre-new-yew">Found this article helpful? Share it:</span>
                <SocialShareButtons url={currentUrl} title={article.title} />
              </div>
            </div>

            {/* Newsletter CTA */}
            <NewsletterSignup />
          </div>
        </article>

        {/* Related Articles */}
        {relatedNews.length > 0 && (
          <section className="section-padding bg-cpre-pebble/30">
            <div className="container-custom max-w-4xl">
              <h2 className="text-2xl font-heading font-bold text-cpre-new-yew mb-8">Related Articles</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {relatedNews.map((related) => {
                  const relatedCard = (
                    <Card className="bg-white border-stone-200 overflow-hidden group hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                      <div className="aspect-video overflow-hidden relative">
                        <img
                          src={resolveNewsImageUrl(related.image_url) || '/images/cpre images for website/13.jpg'}
                          alt={related.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <Badge className={`${categoryColors[related.category] || categoryColors.general} absolute top-4 left-4 text-xs`}>
                          {related.category}
                        </Badge>
                      </div>
                      <CardContent className="p-6">
                        <h3 className="font-heading font-semibold text-lg text-cpre-new-yew group-hover:text-cpre-hill transition-colors duration-200 line-clamp-2">
                          {related.title}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{related.excerpt}</p>
                      </CardContent>
                    </Card>
                  );

                  if (isExternalNewsArticle(related)) {
                    return (
                      <a
                        key={related.id}
                        href={getExternalNewsUrl(related)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {relatedCard}
                      </a>
                    );
                  }

                  return (
                    <Link key={related.id} to={getNewsArticlePath(related)}>
                      {relatedCard}
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
};

export { NewsPage, NewsArticlePage };
export default NewsPage;
